// ── Google Tasks REST client ─────────────────────────────────────────────────
// Talks directly to tasks.googleapis.com using the same access token as
// src/google/calendar.js (both scopes are granted by the one OAuth connection
// — see lib/google-oauth.js's SCOPE). No separate connect/disconnect flow;
// Tasks just rides the Calendar connection.
import { getValidToken, invalidateToken } from './calendar';

const API_BASE = 'https://tasks.googleapis.com/tasks/v1';

async function api(path, { method = 'GET', body, params } = {}) {
  const token = await getValidToken();
  let url = `${API_BASE}${path}`;
  if (params) {
    const qs = new URLSearchParams(params).toString();
    if (qs) url += `?${qs}`;
  }
  const res = await fetch(url, {
    method,
    cache: 'no-store',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    invalidateToken();
    const retryToken = await getValidToken();
    const retry = await fetch(url, {
      method,
      cache: 'no-store',
      headers: {
        Authorization: `Bearer ${retryToken}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!retry.ok) throw await toError(retry);
    return retry.status === 204 ? null : retry.json();
  }
  if (!res.ok) throw await toError(res);
  return res.status === 204 ? null : res.json();
}

async function toError(res) {
  let detail = '';
  try { detail = (await res.json())?.error?.message || ''; } catch {}
  const err = new Error(`Google Tasks API ${res.status}${detail ? `: ${detail}` : ''}`);
  err.status = res.status;
  return err;
}

// ── Task lists ───────────────────────────────────────────────────────────────

// Returns [{ id, title }]
export async function listTaskLists() {
  const data = await api('/users/@me/lists', { params: { maxResults: '100' } });
  return (data?.items || []).map((l) => ({ id: l.id, title: l.title }));
}

// ── Tasks ────────────────────────────────────────────────────────────────────

// Lists every task in a list, including completed/hidden ones, so the sync
// engine can mirror completions and Google-side deletions.
export async function listTasks(taskListId) {
  const out = [];
  let pageToken;
  do {
    const data = await api(`/lists/${encodeURIComponent(taskListId)}/tasks`, {
      params: {
        showCompleted: 'true',
        showHidden: 'true',
        maxResults: '100',
        ...(pageToken ? { pageToken } : {}),
      },
    });
    (data?.items || []).forEach((t) => out.push(t));
    pageToken = data?.nextPageToken;
  } while (pageToken);
  return out;
}

export async function insertTask(taskListId, resource) {
  return api(`/lists/${encodeURIComponent(taskListId)}/tasks`, {
    method: 'POST',
    body: resource,
  });
}

export async function patchTask(taskListId, taskId, resource) {
  return api(`/lists/${encodeURIComponent(taskListId)}/tasks/${encodeURIComponent(taskId)}`, {
    method: 'PATCH',
    body: resource,
  });
}

export async function deleteTask(taskListId, taskId) {
  try {
    await api(`/lists/${encodeURIComponent(taskListId)}/tasks/${encodeURIComponent(taskId)}`, {
      method: 'DELETE',
    });
  } catch (err) {
    // 404/410 → already gone on Google's side; treat as success.
    if (err.status === 404 || err.status === 410) return;
    throw err;
  }
}
